import React, { useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const ChangeEmailModal = ({ domain, show, handleClose, currentEmail }) => {
  const [email, setEmail] = useState("");
  const [verificationStep, setVerificationStep] = useState(0);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [isAuthenticatingVerification, setIsAuthenticatingVerification] = useState(false);
  const [authenticationError, setAuthenticationError] = useState(false);

  const [otp, setOTP] = useState(Array(6).fill(""));
  const inputs = useRef([]);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const sendVerification = () => {
    if (isSendingVerification) return;
    if (!isValidEmail(email)) return;
    setIsSendingVerification(true);

    fetch("/api/users/sendverification", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      redirect: "follow",
    })
      .then((response) => response.text())
      .then(() => {
        setIsSendingVerification(false);
        setAuthenticationError(false);
        setVerificationStep(1);
      })
      .catch((error) => {
        setIsSendingVerification(false);
        setAuthenticationError(false);
        console.error("error", error);
      });
  };

  const submitVerificationCode = () => {
    if (isAuthenticatingVerification) return;
    setIsAuthenticatingVerification(true);

    fetch("/api/users/authenticateverification", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, verificationCode: otp.join("") }),
      redirect: "follow",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Authentication failed");
        return response.json();
      })
      .then((data) => {
        Cookies.set("userId", data.user._id, { expires: 7 });
        Cookies.set("userEmail", data.user.email, { expires: 7 });
        setAuthenticationError(false);
        setIsAuthenticatingVerification(false);
        setVerificationStep(2);
      })
      .catch((error) => {
        setAuthenticationError(true);
        setIsAuthenticatingVerification(false);
        console.error("error", error);
      });
  };

  const backVerificationStep = () => {
    setVerificationStep(verificationStep - 1);
  };

  const handleInputChange = (index, value) => {
    value = value.toUpperCase();
    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);
    if (value && index < 5) {
      inputs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && index > 0 && !otp[index]) {
      const newOTP = [...otp];
      newOTP[index - 1] = "";
      setOTP(newOTP);
      inputs.current[index - 1].focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("Text").toUpperCase();
    const pastedChars = pastedText.split("");
    const newOTP = Array(6).fill("");
    pastedChars.forEach((char, index) => {
      if (index < 6) newOTP[index] = char;
    });
    setOTP(newOTP);
  };

  return (
    <>
      <style>{`
        .change-email-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .change-email-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .change-email-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .change-email-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
        .otp-input {
          width: 44px;
          height: 50px;
          text-align: center;
          font-size: 18px;
          font-weight: 800;
          color: #ff5100;
          background: #fff;
          border: 1.5px solid #e5e7eb;
          border-radius: 10px;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          caret-color: #ff5100;
        }
        .otp-input:focus {
          border-color: #ff5100;
          box-shadow: 0 0 0 3px rgba(255,81,0,0.1);
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        dialogClassName="change-email-modal"
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <i className="fas fa-envelope" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Change Email Address</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                {verificationStep === 0 && "Enter your new email address"}
                {verificationStep === 1 && `Code sent to ${email}`}
                {verificationStep === 2 && "Email updated successfully"}
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {verificationStep === 1 ? (
            <>
              {/* Step 1 — OTP entry */}
              <div
                style={{
                  background: "#f0fdf4", border: "1px solid #bbf7d0",
                  borderRadius: 10, padding: "10px 14px", marginBottom: 16,
                  display: "flex", alignItems: "center", gap: 8,
                }}
              >
                <i className="fas fa-envelope" style={{ color: "#16a34a", fontSize: 13 }} />
                <span style={{ fontSize: 13, color: "#15803d" }}>
                  Verification code sent to <b>{email}</b>. Check your spam folder if you don't see it.
                </span>
              </div>

              {authenticationError && (
                <div
                  style={{
                    background: "#fef2f2", border: "1px solid #fca5a5",
                    borderRadius: 10, padding: "10px 14px", marginBottom: 16,
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  <i className="fas fa-exclamation-circle" style={{ color: "#ef4444", fontSize: 13 }} />
                  <span style={{ fontSize: 13, color: "#991b1b" }}>
                    Invalid code. Please enter the latest code sent to your email.
                  </span>
                </div>
              )}

              <div style={{
                display: "flex", justifyContent: "center", gap: 8, margin: "8px 0 16px",
              }}>
                {Array.from({ length: 6 }, (_, index) => (
                  <input
                    ref={(input) => (inputs.current[index] = input)}
                    key={index}
                    className="otp-input"
                    type="text"
                    maxLength="1"
                    value={otp[index]}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    placeholder="·"
                  />
                ))}
              </div>

              <div style={{ textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
                Didn't receive it?{" "}
                <button
                  type="button"
                  onClick={backVerificationStep}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "#ff5100", fontWeight: 600, fontSize: 13, padding: 0,
                  }}
                >
                  Send again
                </button>
              </div>
            </>
          ) : verificationStep === 2 ? (
            /* Step 2 — Success */
            <div style={{ textAlign: "center", padding: "20px 0 12px" }}>
              <div
                style={{
                  width: 72, height: 72, borderRadius: "50%", margin: "0 auto 16px",
                  background: "rgba(255,81,0,0.08)", border: "2px solid rgba(255,81,0,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <i className="fas fa-check" style={{ fontSize: 28, color: "#ff5100" }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", marginBottom: 8 }}>All set!</div>
              <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>
                Thank you for verifying, <b>{email}</b>.<br />
                You may now resume your activity.
              </div>
            </div>
          ) : (
            /* Step 0 — Enter new email */
            <>
              <div
                style={{
                  background: "#fffbeb", border: "1px solid #fcd34d",
                  borderRadius: 10, padding: "10px 14px", marginBottom: 16,
                  display: "flex", alignItems: "flex-start", gap: 8,
                }}
              >
                <i className="fas fa-info-circle" style={{ color: "#d97706", fontSize: 13, marginTop: 1 }} />
                <span style={{ fontSize: 13, color: "#92400e" }}>
                  Your current email is <b>{currentEmail}</b>. Enter your new email address below. We'll send a 6-character verification code to confirm it.
                </span>
              </div>

              <div>
                <div style={{ color: "#6b7280", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>
                  New Email Address <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  style={inputStyle}
                  {...focusHandlers}
                />
              </div>

              <div style={{ marginTop: 14, fontSize: 12, color: "#9ca3af" }}>
                By proceeding, you accept our{" "}
                <Link to="/termsofservice" target="_blank" style={{ color: "#ff5100", fontWeight: 600 }}>
                  Terms of Service
                </Link>
                .
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {verificationStep === 1 ? (
            <button
              type="button"
              onClick={submitVerificationCode}
              disabled={isAuthenticatingVerification}
              style={{
                width: "100%", padding: "11px 0", borderRadius: 10, border: "none",
                background: isAuthenticatingVerification ? "#e5e7eb" : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
                color: isAuthenticatingVerification ? "#9ca3af" : "#fff",
                fontWeight: 700, fontSize: 15,
                cursor: isAuthenticatingVerification ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 9, transition: "background 0.15s",
              }}
            >
              {isAuthenticatingVerification ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: 13 }} />
                  Verifying…
                </>
              ) : (
                <>
                  Proceed
                  <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
                </>
              )}
            </button>
          ) : verificationStep === 2 ? (
            <button
              type="button"
              onClick={handleClose}
              style={{
                width: "100%", padding: "11px 0", borderRadius: 10, border: "none",
                background: "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
                color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 9,
              }}
            >
              Done
              <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
            </button>
          ) : (
            <button
              type="button"
              onClick={sendVerification}
              disabled={isSendingVerification || !isValidEmail(email)}
              style={{
                width: "100%", padding: "11px 0", borderRadius: 10, border: "none",
                background: (isSendingVerification || !isValidEmail(email)) ? "#e5e7eb" : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
                color: (isSendingVerification || !isValidEmail(email)) ? "#9ca3af" : "#fff",
                fontWeight: 700, fontSize: 15,
                cursor: (isSendingVerification || !isValidEmail(email)) ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 9, transition: "background 0.15s",
              }}
            >
              {isSendingVerification ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: 13 }} />
                  Sending code…
                </>
              ) : (
                <>
                  Send Verification Code
                  <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
                </>
              )}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChangeEmailModal;
